import { useCallback, useRef } from "react";
import { cn } from "@/lib/cn";
import { useWindowOrigin } from "@/desktop/use-window-origin";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { useActiveDisplay } from "@/desktop/use-active-display";
import { focusConversationFrameLayout } from "@/lib/conversation-focus-layout";
import { AgentCursor } from "@/components/cursors/agent-cursor";
import { useCursorAnimation } from "@/components/cursors/use-cursor-animation";
import type { CursorAnimationState } from "@/components/cursors/use-cursor-animation";
import type { AgentCursor as AgentCursorType } from "@/stores/canvas-store";
import { AgentAnnotationOverlays } from "@/components/cursors/agent-annotation-overlay";
import { cursorBubbleExclusions } from "@/components/cursors/cursor-bubble-exclusions";
import { agentCursorDisplayStatus } from "@/components/cursors/agent-cursor-display-status";
import { isCursorPresenting } from "@/components/cursors/agent-cursor-terminal-status";
import { useActiveGuidePresentationId } from "@/components/cursors/use-active-guide-presentation-id";
import {
  canvasCursorPaintsOnDisplay,
  useFrameWindowDisplayId,
} from "@/components/cursors/agent-cursor-layer-visibility";
import { isTauri } from "@/lib/is-tauri";
import { useRuntimeCursorPill } from "./use-runtime-cursor-pill";

/** One positioned, animated cursor within {@link AgentCursorLayer}. Split out
 *  so the layer's own file (store reads, cursor-entry derivation) stays
 *  under the file-length limit. */
export function AnimatedCursor({
  cursor,
  bandIndex,
  isNew,
  spawnAnchor,
  resolvedColor,
  stalledSinceMs,
  suppressStatus,
  owningConversationFrameId,
  ownsQuestion,
  onCancel,
  onSwitchModel,
  onDismiss,
}: {
  cursor: AgentCursorType;
  bandIndex: number;
  isNew: boolean;
  spawnAnchor: { x: number; y: number } | null;
  resolvedColor: string;
  stalledSinceMs?: number;
  suppressStatus?: boolean;
  owningConversationFrameId?: string | null;
  /** The cursor is its run's one surface for the question it asks. */
  ownsQuestion: boolean;
  onCancel?: () => void;
  onSwitchModel?: () => void;
  onDismiss?: () => void;
}) {
  const divRef = useRef<HTMLDivElement>(null);
  const winOrigin = useWindowOrigin();
  const myDisplayId = getWindowDisplayId();
  const activeDisplayId = useActiveDisplay((s) => s.activeDisplayId);
  const activeGuideId = useActiveGuidePresentationId();
  const pointTarget =
    cursor.pointTarget &&
    (myDisplayId === null || cursor.pointTarget.displayId === myDisplayId)
      ? {
          x: cursor.pointTarget.globalX - winOrigin.x,
          y: cursor.pointTarget.globalY - winOrigin.y,
        }
      : null;
  const bubbleExclusions = cursorBubbleExclusions(cursor, winOrigin, {
    width: window.innerWidth,
    height: window.innerHeight,
  });
  // In the app, a pointing cursor that is its run's one surface asks its
  // question in full beside the arrow; otherwise it keeps the badge.
  const ownsPointedQuestion = !!pointTarget && ownsQuestion;
  const presenceAllowed = isTauri() && !suppressStatus;
  const desktopPresence =
    presenceAllowed && (!pointTarget || ownsPointedQuestion);
  const runtimePill = useRuntimeCursorPill({
    current: desktopPresence ? cursor : null,
    // Hover holds an answered question's confirm after it leaves the cursor.
    visible: presenceAllowed,
    origin: winOrigin,
    displayId: myDisplayId ?? activeDisplayId ?? undefined,
    exclusions: bubbleExclusions,
  });
  const useSharedPill =
    (desktopPresence ||
      (presenceAllowed && !!pointTarget && !!runtimePill.cursor?.question)) &&
    (!cursor.question || !!runtimePill.questionToast);

  const holdReplyPositionRef = useRef(false);
  holdReplyPositionRef.current = !!(
    useSharedPill &&
    runtimePill.hovered &&
    runtimePill.interactive &&
    runtimePill.content?.kind === "reply" &&
    !runtimePill.content.terminal &&
    !isCursorPresenting(cursor, activeGuideId)
  );

  // Direct DOM mutation — bypasses React reconciliation for 60fps performance
  const handleUpdate = useCallback((state: CursorAnimationState) => {
    // Keep the visible reply reachable even while a new frame action travels
    // underneath it. The engine continues, and resumes here on the next tick
    // after hover ends. Its point completions and working stages stay intact.
    if (
      divRef.current &&
      (!holdReplyPositionRef.current || !divRef.current.style.transform)
    ) {
      divRef.current.style.transform = `translate(${state.x}px, ${state.y}px)`;
    }
  }, []);

  // The cursor follows the live user pointer for the entire duration
  // of the underlying execution (thinking, tool calls, streaming
  // assistant text — all part of one continuous "the agent is working"
  // window from the user's POV). The follow stops when:
  //   - the execution reaches a terminal state — `frozen` is set by
  //     the companion frame reducer when execution.completed/failed/
  //     cancelled/paused fires. The post-completion grace window leaves the bubble
  //     visible so the user can read a long answer (and click the
  //     expand arrow); freezing snaps it to the pointer position from
  //     the moment the agent finished.
  //   - a cursor-anchored question lands — the click targets must stay
  //     under the pointer the user actually moves toward.
  //   - an inline error is rendered — error chip is interactive too.
  //   - the cursor has a frame target — the working sequence overrides
  //     the idle anchor with frame-relative travel/drift.
  const hasExpandableReply = !!(
    !useSharedPill &&
    cursor.contentFull &&
    cursor.content &&
    cursor.contentFull.length > cursor.content.length
  );
  const followLivePointer =
    !cursor.targetFrameId &&
    !cursor.frozen &&
    !cursor.error &&
    !cursor.question &&
    !hasExpandableReply &&
    stalledSinceMs === undefined &&
    !runtimePill.freezePosition;
  const freezePosition = !!(
    runtimePill.freezePosition ||
    cursor.error ||
    cursor.question ||
    hasExpandableReply ||
    stalledSinceMs !== undefined
  );
  useCursorAnimation({
    targetFrameId: cursor.targetFrameId,
    actionSeq: cursor.actionSeq ?? null,
    pointTarget,
    pointSeq: cursor.pointTarget?.seq ?? null,
    travelDurationMs: cursor.pointTarget?.travelDurationMs,
    completionToken: cursor.pointTarget?.completionToken,
    onUpdate: handleUpdate,
    bandIndex,
    spawnAnchor,
    followLivePointer,
    freezePosition,
    ambientMotion: !desktopPresence,
  });

  // Re-paint when the target frame opens or closes in a window elsewhere.
  useFrameWindowDisplayId(cursor.targetFrameId);
  const annotationOverlays = isTauri() ? null : (
    <AgentAnnotationOverlays cursor={cursor} variant="workspace" />
  );
  if (!canvasCursorPaintsOnDisplay(cursor, myDisplayId, activeDisplayId)) {
    return <>{annotationOverlays}</>;
  }
  const displayStatus = agentCursorDisplayStatus(cursor, isTauri());
  const isPointing = !!pointTarget;

  return (
    <>
      {annotationOverlays}
      <div
        ref={divRef}
        className={cn("absolute", isNew && "animate-fade-in")}
        data-interactive={
          useSharedPill && runtimePill.interactive ? "true" : undefined
        }
      >
        <AgentCursor
          agentName={cursor.name}
          agentColor={resolvedColor}
          toolCall={displayStatus.toolCall}
          toolFailed={displayStatus.toolFailed}
          content={displayStatus.content}
          contentFull={displayStatus.contentFull}
          thinking={displayStatus.thinking}
          error={displayStatus.error}
          questionAwaitingInputId={
            useSharedPill ? undefined : displayStatus.question?.awaitingInputId
          }
          onQuestionExpandMissed={
            owningConversationFrameId
              ? () => focusConversationFrameLayout(owningConversationFrameId)
              : undefined
          }
          statusPill={useSharedPill ? runtimePill.statusPill : undefined}
          suppressStatus={suppressStatus}
          stalledSinceMs={stalledSinceMs}
          onCancel={onCancel}
          onSwitchModel={onSwitchModel}
          onDismiss={onDismiss}
          statusDraggable={
            !!onDismiss && !isCursorPresenting(cursor, activeGuideId)
          }
          pointing={isPointing}
          bubbleExclusions={bubbleExclusions}
          data-presentation-delivery-id={cursor.presentationDeliveryId}
          data-testid={`agent-cursor-${cursor.agentId}`}
        />
      </div>
    </>
  );
}
